import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import StatsGrid from '@/components/StatsGrid';
import StatCard from '@/components/StatCard';
import DataTableCard from '@/components/DataTableCard';
import EmptyState from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Users as UsersIcon, Plus, Edit, Trash2 } from 'lucide-react';

const STATUSES = ['active', 'prospect', 'inactive'];

const statusColors = {
  active: 'bg-green-100 text-green-800',
  prospect: 'bg-yellow-100 text-yellow-800',
  inactive: 'bg-gray-100 text-gray-800',
};

const Clients = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const [clientsRes, statsRes] = await Promise.all([
        api.get('/clients'),
        api.get('/clients/stats/summary'),
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load clients');
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (!['admin', 'senior_partner', 'associate'].includes(user.role)) {
        toast.error('Access denied. Staff privileges required.');
        navigate('/');
        return;
      }
      fetchData();
    }
  }, [user, loading, navigate, fetchData]);

  const openCreate = () => navigate('/clients/new');
  const openEdit = (client) => navigate(`/clients/${client.id}/edit`);

  const deleteClient = async (client) => {
    if (!window.confirm(`Delete client "${client.display_name}"?`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success('Client deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete client');
    }
  };

  const visible = clients.filter(
    (c) =>
      (statusFilter === 'all' || c.status === statusFilter) &&
      (!search ||
        (c.display_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (c.notes || '').toLowerCase().includes(search.toLowerCase()))
  );

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <PageShell>
      <PageHeader
        title="Clients"
        icon={UsersIcon}
        titleTestId="clients-title"
        actions={
          <Button onClick={openCreate} data-testid="new-client-btn">
            <Plus size={20} /> New Client
          </Button>
        }
      />

      <StatsGrid cols={4}>
        <StatCard value={stats.total || 0} label="Total" onClick={() => setStatusFilter('all')} />
        <StatCard value={stats.active || 0} label="Active" valueClassName="text-green-600" onClick={() => setStatusFilter('active')} />
        <StatCard value={stats.prospect || 0} label="Prospect" valueClassName="text-yellow-600" onClick={() => setStatusFilter('prospect')} />
        <StatCard value={stats.inactive || 0} label="Inactive" valueClassName="text-gray-600" onClick={() => setStatusFilter('inactive')} />
      </StatsGrid>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Input
          placeholder="Search clients..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:w-80"
          data-testid="client-search"
        />
        <select
          className="w-full sm:w-48 border rounded-md px-3 py-2 text-sm bg-background"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="client-status-filter"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={UsersIcon} message="No clients found" />
      ) : (
        <DataTableCard>
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-3 font-medium" scope="col">Name</th>
                <th className="p-3 font-medium" scope="col">Type</th>
                <th className="p-3 font-medium" scope="col">Status</th>
                <th className="p-3 font-medium" scope="col">Primary contact</th>
                <th className="p-3 font-medium" scope="col">Billing email</th>
                <th className="p-3 font-medium text-right" scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`client-row-${c.id}`}>
                  <td className="p-3 font-medium">{c.display_name || '—'}</td>
                  <td className="p-3 capitalize">{c.type}</td>
                  <td className="p-3">
                    <Badge className={statusColors[c.status]}>{c.status}</Badge>
                  </td>
                  <td className="p-3">{c.primary_contact_name || '—'}</td>
                  <td className="p-3">{c.billing_email || '—'}</td>
                  <td className="p-3">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-client-${c.id}`}>
                        <Edit size={16} /> Edit
                      </Button>
                      {user.role !== 'associate' && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => deleteClient(c)}
                          data-testid={`delete-client-${c.id}`}
                          aria-label={`Delete client ${c.display_name}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableCard>
      )}
    </PageShell>
  );
};

export default Clients;
